export interface ArticleSection {
  heading: string;
  paragraphs: string[];
}

export interface TimelineItem {
  time: string;
  title: string;
  desc: string;
}

export interface Screenshot {
  url: string;
  alt: string;
  caption: string;
}

export interface Article {
  id: string;
  title: string;
  subtitle: string;
  summary: string;
  category: string;
  tags: string[];
  author: string;
  publishedAt: string;
  updatedAt: string;
  readMinutes: number;
  heat: number;
  cover: string;
  coverAlt: string;
  featured: boolean;
  originalPost: {
    platform: string;
    handle: string;
    time: string;
    text: string;
  };
  screenshots: Screenshot[];
  timeline: TimelineItem[];
  sections: ArticleSection[];
  followUp: string[];
}

export const articles: Article[] = [
  {
    id: "a-1001",
    title: "「甜心柚子」直播间哽咽下播：一场带货事故的全过程还原",
    subtitle: "从口误到刷屏，只隔了四十分钟",
    summary:
      "一场原本平平无奇的美食带货直播，因为一句口误和随后失控的弹幕，成了本周最出圈的重磅爆料。我们把当晚的直播回放、原帖和后续回应按顺序整理在一起。",
    category: "baoliao",
    tags: ["直播间", "带货翻车", "当事人回应"],
    author: "瓜田记录员",
    publishedAt: "2026-09-06",
    updatedAt: "2026-09-09",
    readMinutes: 7,
    heat: 98600,
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20warm%20lit%20streaming%20studio%20corner%20with%20a%20ring%20light%20and%20blurred%20product%20shelves%2C%20no%20people%2C%20crimson%20and%20cream%20color%20tones%2C%20editorial%20magazine%20photography%2C%20soft%20cinematic%20grain&width=1000&height=640&seq=cover-1&orientation=landscape",
    coverAlt: "带货直播间氛围配图，暖光与环形灯",
    featured: true,
    originalPost: {
      platform: "短视频平台",
      handle: "@吃瓜第一排",
      time: "9 月 6 日 21:14",
      text: "刚看完直播回放，感觉气氛从第 32 分钟开始就不对劲了……弹幕区已经完全停不下来，我把关键几段截了图，大家自己看吧。",
    },
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=abstract%20candid%20photo%20of%20a%20crowded%20live%20chat%20comment%20stream%20rendered%20as%20blurred%20glowing%20text%20bars%20on%20a%20dark%20interface%2C%20no%20readable%20words%2C%20red%20and%20amber%20accents%2C%20editorial%20screenshot%20aesthetic&width=800&height=600&seq=shot-1&orientation=landscape",
        alt: "直播弹幕刷屏的抽象截图示意",
        caption: "当晚 21:32 之后的弹幕区，关键词密集出现",
      },
      {
        url: "https://readdy.ai/api/search-image?query=abstract%20product%20table%20on%20a%20studio%20desk%20with%20unlabeled%20packaging%20boxes%20and%20a%20hand%20written%20note%2C%20no%20people%2C%20warm%20cream%20background%2C%20soft%20shadow%20editorial%20photography&width=800&height=600&seq=shot-2&orientation=landscape",
        alt: "涉事商品与手写备注的抽象示意图",
        caption: "被反复截图讨论的商品陈列画面",
      },
      {
        url: "https://readdy.ai/api/search-image?query=abstract%20smartphone%20screen%20glowing%20in%20a%20dark%20room%20showing%20a%20notification%20list%2C%20no%20readable%20text%2C%20crimson%20accent%20glow%2C%20moody%20editorial%20photography%20with%20shallow%20depth%20of%20field&width=800&height=600&seq=shot-3&orientation=landscape",
        alt: "手机通知列表的抽象截图示意",
        caption: "下播后账号发布的第一条动态",
      },
    ],
    timeline: [
      { time: "20:58", title: "直播开始", desc: "在线人数约 1.2 万，节奏与往常无异，主推当季零食组合。" },
      { time: "21:32", title: "口误出现", desc: "介绍产品规格时表述出现偏差，弹幕开始追问。" },
      { time: "21:40", title: "弹幕失控", desc: "相关关键词刷屏，助播多次提醒回到商品本身。" },
      { time: "21:52", title: "短暂离开镜头", desc: "主播调整状态后返回，声音明显哽咽。" },
      { time: "22:03", title: "提前下播", desc: "直播结束，未按计划完成后续两个品类的讲解。" },
      { time: "次日 10:20", title: "发布回应", desc: "账号发布说明，承认表述失误并给出处理方案。" },
    ],
    sections: [
      {
        heading: "事情是怎么开始的",
        paragraphs: [
          "9 月 6 日晚的这场直播，原本只是一次常规的零食专场。前半小时的节奏非常平顺，主播按脚本依次介绍品类，助播负责补充库存和优惠信息，弹幕区大多是询问口味和发货时间。",
          "转折发生在第 32 分钟。在介绍某款组合装的规格时，主播的表述与商品详情页出现了出入，弹幕立刻有人指出。最初只是零星几条追问，但很快被更多围观者放大，气氛开始变得微妙。",
        ],
      },
      {
        heading: "弹幕为什么突然失控",
        paragraphs: [
          "从回放看，真正的加速点是助播的一句「先别刷了，我们按流程讲」。这句本意是维持秩序的话，被部分观众解读成了回避，弹幕量在接下来三分钟内翻了数倍。",
          "与此同时，有观众把直播画面与商品页截图拼在一起发到其他平台，事件开始从直播间溢出到站外，带来第二波、第三波围观流量。到 21 点 52 分，直播间同时在线人数已经是开播时的三倍多。",
        ],
      },
      {
        heading: "当事人如何回应",
        paragraphs: [
          "次日早上，账号发布了一份简短说明，承认当晚对产品规格的表述存在错误，并给出了两个处理方向：已下单用户可无理由退货，未下单用户可获得一次专属折扣补偿。",
          "说明中没有回避责任，也没有把问题推给「个别网友带节奏」，这一点在评论区获得了相对正面的评价。但仍有不少观众认为，问题在于直播选品环节的审核不够严谨，补偿只是补救。",
        ],
      },
    ],
    followUp: [
      "涉事商品已从当季主推位撤下，详情页规格说明做了补充更新。",
      "账号宣布暂停日常直播三天，用于复盘选品与脚本审核流程。",
      "多位同行主播公开表态，讨论直播口播稿的二次核对机制。",
    ],
  },
  {
    id: "a-1002",
    title: "探店博主「夜猫阿哲」同款脚本被扒：一场关于「真实探店」的争论",
    subtitle: "三家店的文案高度相似，网友开始逐帧对比",
    summary:
      "有网友发现，探店博主「夜猫阿哲」近三个月发布的三条探店视频，台词结构与运镜顺序高度一致，由此引发对「探店是否真实」的广泛讨论。",
    category: "jingtian",
    tags: ["探店", "脚本争议", "逐帧对比"],
    author: "瓜田记录员",
    publishedAt: "2026-09-02",
    updatedAt: "2026-09-08",
    readMinutes: 8,
    heat: 87400,
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20night%20street%20restaurant%20storefront%20glowing%20warmly%20with%20blurred%20neon%20signs%2C%20no%20people%2C%20cinematic%20urban%20food%20documentary%20mood%2C%20crimson%20and%20amber%20reflections%20on%20wet%20pavement&width=1000&height=640&seq=cover-2&orientation=landscape",
    coverAlt: "夜间街边餐饮店氛围配图",
    featured: true,
    originalPost: {
      platform: "图文社区",
      handle: "@脚本研究所",
      time: "9 月 2 日 18:40",
      text: "闲着没事把阿哲最近三条视频的时间轴对了一遍，台词节点几乎一模一样，连停顿位置都对得上。做了张对比表，大家理性讨论。",
    },
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=abstract%20side%20by%20side%20video%20timeline%20comparison%20board%20with%20colored%20markers%2C%20no%20readable%20text%2C%20dark%20editorial%20interface%2C%20amber%20and%20crimson%20highlights&width=800&height=600&seq=shot-4&orientation=landscape",
        alt: "三条视频时间轴对比的抽象示意图",
        caption: "网友制作的时间轴对比表，三个节点完全重合",
      },
      {
        url: "https://readdy.ai/api/search-image?query=abstract%20video%20editing%20timeline%20on%20a%20large%20monitor%20in%20a%20dark%20room%2C%20no%20readable%20text%2C%20warm%20amber%20waveform%20glow%2C%20editorial%20behind%20the%20scenes%20photography&width=800&height=600&seq=shot-5&orientation=landscape",
        alt: "视频剪辑时间轴的抽象示意图",
        caption: "被指结构雷同的三条视频剪辑工程对比",
      },
    ],
    timeline: [
      { time: "9 月 2 日 18:40", title: "对比帖发布", desc: "图文社区出现时间轴对比帖，迅速冲上本地话题榜。" },
      { time: "9 月 2 日 23:10", title: "站外扩散", desc: "对比图被搬运到短视频平台，单条播放破百万。" },
      { time: "9 月 3 日 14:00", title: "博主回应", desc: "夜猫阿哲发布视频，承认使用统一拍摄模板。" },
      { time: "9 月 4 日", title: "商家发声", desc: "其中两家餐厅表示并未提前沟通过拍摄内容。" },
      { time: "9 月 8 日", title: "规则更新", desc: "博主宣布今后探店视频标注「商单」或「自费」。" },
    ],
    sections: [
      {
        heading: "对比表是怎么来的",
        paragraphs: [
          "对比帖的作者自称是一名剪辑师，出于职业习惯把三条视频导入工程里对齐。让他意外的是，三条视频不仅开场白结构一致，连镜头切换的节奏都能对上：进店、落座、上菜、第一口、总结，每个环节时长误差都在两秒以内。",
          "这份对比表发布后很快被搬运，评论区两种声音各占一半。一部分人认为，模板化是行业常态，不必上纲上线；另一部分人质疑的是，视频里那种「第一次来、随手发现的惊喜感」是否属于刻意营造。",
        ],
      },
      {
        heading: "博主的回应与关键信息",
        paragraphs: [
          "9 月 3 日下午，夜猫阿哲发布了一条十秒的回应视频。他承认团队会使用统一的拍摄模板来保证出片效率，但强调每一家店都是真实到访、真实试吃。",
          "回应里最关键的一句是「模板可以统一，但感受不能造假」。这句话被大量引用，也成了争论的新焦点：如果感受被模板框住，还能算真实吗？",
        ],
      },
      {
        heading: "这件事真正暴露了什么",
        paragraphs: [
          "争议延续到 9 月 4 日，两家被拍摄的餐厅公开表示，并未提前与团队沟通拍摄内容，也没有约定推广形式。这让讨论从「模板是否合理」转向了「探店内容与商业合作是否该被标注」。",
          "9 月 8 日，博主宣布后续探店视频将在开头明确标注「商单」或「自费」。这个处理方式没有平息所有质疑，但至少把行业里长期模糊的一条线，摆到了台面上。",
        ],
      },
    ],
    followUp: [
      "探店视频开头新增「商单 / 自费」标注，首条已上线。",
      "多家同行跟进标注规则，相关话题阅读量持续增长。",
      "对比帖作者补充了第四组对比素材，讨论仍在继续。",
    ],
  },
  {
    id: "a-1003",
    title: "美食博主「一口苗苗」配方争议：原帖、截图与回应整理",
    subtitle: "一份被指「来源不明」的配方，牵出了半年前的旧视频",
    summary:
      "围绕一份家常甜点配方，「一口苗苗」与另一位创作者之间产生了来源争议。我们把双方原帖、时间线和沟通截图整理在一起。",
    category: "mengliao",
    tags: ["配方争议", "原创归属", "沟通截图"],
    author: "瓜田记录员",
    publishedAt: "2026-08-28",
    updatedAt: "2026-09-05",
    readMinutes: 6,
    heat: 63200,
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20rustic%20kitchen%20counter%20with%20flour%20dust%2C%20mixing%20bowls%20and%20a%20notebook%20of%20handwritten%20recipe%20cards%2C%20no%20people%2C%20warm%20cream%20and%20amber%20tones%2C%20soft%20natural%20window%20light%2C%20editorial%20food%20photography&width=1000&height=640&seq=cover-3&orientation=landscape",
    coverAlt: "厨房台面与配方笔记配图",
    featured: false,
    originalPost: {
      platform: "图文社区",
      handle: "@小碗厨房日记",
      time: "8 月 28 日 12:05",
      text: "翻到半年前自己发的配方笔记，发现文字几乎被整段搬走，连我写错的克数都一模一样。我没有别的意思，只想要一个说明。",
    },
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=abstract%20two%20pieces%20of%20paper%20with%20blurred%20handwriting%20side%20by%20side%20on%20a%20warm%20wooden%20table%2C%20no%20readable%20text%2C%20red%20circled%20highlights%2C%20editorial%20comparison%20photography&width=800&height=600&seq=shot-6&orientation=landscape",
        alt: "两份配方笔记并排对比的抽象示意图",
        caption: "双方配方笔记的对比，圈出部分为重合表述",
      },
      {
        url: "https://readdy.ai/api/search-image?query=abstract%20chat%20conversation%20bubbles%20blurred%20on%20a%20dark%20phone%20screen%2C%20no%20readable%20text%2C%20amber%20and%20cream%20tones%2C%20editorial%20messaging%20screenshot%20aesthetic&width=800&height=600&seq=shot-7&orientation=landscape",
        alt: "双方沟通记录抽象截图",
        caption: "双方私信沟通的截图，由原帖作者提供",
      },
    ],
    timeline: [
      { time: "8 月 28 日 12:05", title: "原帖发布", desc: "小碗厨房日记发文指出配方疑似被整段搬运。" },
      { time: "8 月 28 日 20:30", title: "评论区发酵", desc: "大量用户涌入双方评论区，话题进入本地热榜。" },
      { time: "8 月 29 日 11:00", title: "首次回应", desc: "一口苗苗发文称配方来源于家中长辈口述。" },
      { time: "8 月 30 日", title: "补充材料", desc: "原帖作者公布私信记录，双方说法出现分歧。" },
      { time: "9 月 5 日", title: "达成共识", desc: "双方同时发文，视频标注参考来源并互相致意。" },
    ],
    sections: [
      {
        heading: "争议的核心是什么",
        paragraphs: [
          "这不是一场典型的「谁抄谁」大战，双方的争点其实很具体：一份家常配方到底算不算需要署名的原创内容。原帖作者认为，自己整理的比例和步骤是半年试错的结果，被整段使用却没有任何说明。",
          "而另一位当事人的说法是，配方来自家中长辈口述，自己只是把记忆里的比例写下来，并不知道网上已有几乎相同的版本。两种说法并不完全冲突，但缺少一个能证明先后的时间证据。",
        ],
      },
      {
        heading: "双方的沟通与分歧",
        paragraphs: [
          "8 月 30 日公布私信记录后，讨论变得更聚焦。记录显示，两人在最初沟通时语气都比较克制，主要分歧在于「是否需要在视频里提及来源」。",
          "一条被反复讨论的细节是：双方在配方的某一个步骤上写了完全一样的备注，包括一个不常见的火候描述。这成了很多人判断来源的依据，但它依然只是推测。",
        ],
      },
      {
        heading: "最终怎么收场",
        paragraphs: [
          "9 月 5 日，双方同时发文，宣布已达成一致：相关视频将在简介中标注「配方参考自小碗厨房日记」，原帖作者也撤下了部分措辞较重的表述。",
          "这件事没有赢家，但它给内容创作者提了个醒：涉及具体比例、步骤这类可复制的内容，标注来源的成本很低，收益却很高。",
        ],
      },
    ],
    followUp: [
      "相关视频简介已补充来源标注。",
      "原帖作者更新了素材授权说明，方便他人规范引用。",
      "话题热度回落，讨论转向创作者内容授权规则。",
    ],
  },
  {
    id: "a-1004",
    title: "年度大瓜盘点：这一年最出圈的十场网红风波",
    subtitle: "反转、回应、后续，一次看完",
    summary:
      "我们把这一年里讨论度最高的十场网红风波按热度排了个序，标出每一场的反转次数、关键回应和最终走向，方便你一次补完。",
    category: "niandu",
    tags: ["年度盘点", "名场面", "热度榜"],
    author: "瓜田编辑部",
    publishedAt: "2026-08-20",
    updatedAt: "2026-09-01",
    readMinutes: 10,
    heat: 112000,
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20grid%20of%20glossy%20editorial%20magazine%20covers%20in%20warm%20champagne%20and%20crimson%20tones%20on%20a%20clean%20studio%20surface%2C%20no%20people%2C%20year%20in%20review%20aesthetic%2C%20soft%20directional%20lighting&width=1000&height=640&seq=cover-4&orientation=landscape",
    coverAlt: "年度盘点杂志封面排列配图",
    featured: true,
    originalPost: {
      platform: "站内编辑部",
      handle: "@瓜田编辑部",
      time: "8 月 20 日 09:00",
      text: "又是一年。我们把这一年真正刷屏过的风波重新翻了一遍，按热度排了序。排名只看讨论量，不代表我们对事件本身的判断。",
    },
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=abstract%20bar%20chart%20made%20of%20glowing%20crimson%20and%20amber%20columns%20rising%20on%20a%20dark%20panel%2C%20no%20readable%20text%2C%20editorial%20data%20visualization%20style&width=800&height=600&seq=shot-8&orientation=landscape",
        alt: "年度热度排行柱状图抽象示意",
        caption: "年度风波热度排行（示意图，仅代表站内统计口径）",
      },
    ],
    timeline: [
      { time: "1 月—3 月", title: "第一季度", desc: "以直播间口误和选品争议为主，平均三天一场讨论高峰。" },
      { time: "4 月—6 月", title: "第二季度", desc: "探店与配方类内容归属争议集中出现。" },
      { time: "7 月—8 月", title: "第三季度", desc: "情侣账号与合作方纠纷成为主线。" },
      { time: "9 月", title: "第四季度初", desc: "跨平台连环发酵的超级大瓜开始增多。" },
    ],
    sections: [
      {
        heading: "这一年的大瓜有什么共同点",
        paragraphs: [
          "翻完一整年的记录，最明显的规律是：真正出圈的事件，起点往往都很小。一句口误、一张对比图、一条被截图保存的评论，都可能成为引线。",
          "第二个规律是回应速度。事件爆发后的黄金两小时里，有没有一个明确、具体、不推责的回应，几乎决定了后续走向。含糊其辞的回应，通常会让讨论延长三到五天。",
        ],
      },
      {
        heading: "热度最高的三类事件",
        paragraphs: [
          "第一类是直播相关，因为实时性和不可控性，最容易产生意外。第二类是内容归属争议，它涉及创作者的切身利益，同行参与度高。第三类是账号之间的公开纠纷，因为双方都有粉丝基础，讨论容易形成对立。",
          "值得注意的是，今年不少事件的最终走向都不是「一方认输」，而是规则被推动着更新了一次，比如标注规范、来源说明、退费流程。这或许是个好信号。",
        ],
      },
      {
        heading: "怎么理性地吃瓜",
        paragraphs: [
          "看大瓜最容易踩的坑，是把「爆料」当成「结论」。一份聊天记录截图、一段语气激烈的语音，只能说明有人提出了主张，不能自动等于事实成立。",
          "比较稳妥的方式是等一等：等时间线完整、等另一方回应、等关键证据出现。很多时候，第二天看到的信息会让第一天的判断完全反转。",
        ],
      },
    ],
    followUp: [
      "年度榜单将于每季度更新一次，纳入新出现的高讨论度事件。",
      "编辑部同步整理了「吃瓜理性指南」，可在关于我们页面查看内容原则。",
    ],
  },
  {
    id: "a-1005",
    title: "旅行博主「山河小鹿」滤镜争议后续：当事人发布原片对比",
    subtitle: "同一个观景台，两种颜色的天空",
    summary:
      "一位旅行博主发布的观景台照片与实拍差异较大，引发讨论。三天后，当事人主动发布原片与成片对比，回应质疑。",
    category: "baoliao",
    tags: ["滤镜", "原片对比", "旅行内容"],
    author: "瓜田记录员",
    publishedAt: "2026-08-25",
    updatedAt: "2026-08-30",
    readMinutes: 6,
    heat: 54100,
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20mountaintop%20viewpoint%20platform%20at%20dusk%20with%20layered%20ridges%20and%20soft%20gradient%20sky%2C%20no%20people%2C%20cinematic%20travel%20documentary%20mood%2C%20warm%20amber%20and%20muted%20rose%20tones%2C%20atmospheric%20haze&width=1000&height=640&seq=cover-5&orientation=landscape",
    coverAlt: "山顶观景台黄昏景色配图",
    featured: false,
    originalPost: {
      platform: "短视频平台",
      handle: "@路上随手拍",
      time: "8 月 25 日 16:20",
      text: "同一个位置，我拍的和博主拍的感觉不是同一个地方。不是说不能修图，但差距太大容易让人白跑一趟。",
    },
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=abstract%20two%20landscape%20photos%20side%20by%20side%2C%20one%20vivid%20saturated%20one%20muted%20gray%2C%20mountain%20silhouettes%2C%20no%20people%2C%20editorial%20before%20and%20after%20comparison%20layout&width=800&height=600&seq=shot-9&orientation=landscape",
        alt: "原片与成片对比抽象示意",
        caption: "当事人公布的原片（左）与成片（右）对比",
      },
    ],
    timeline: [
      { time: "8 月 25 日 16:20", title: "质疑帖出现", desc: "游客发布同位置实拍，指出与博主成片差距明显。" },
      { time: "8 月 26 日", title: "话题扩散", desc: "讨论从单个博主扩展到旅行内容修图尺度。" },
      { time: "8 月 28 日 19:00", title: "主动回应", desc: "山河小鹿发布原片对比视频，说明调色范围。" },
      { time: "8 月 30 日", title: "补充说明", desc: "博主在简介中新增「实拍时间与天气说明」。" },
    ],
    sections: [
      {
        heading: "差距到底有多大",
        paragraphs: [
          "质疑帖里最有力的证据，是游客在同一时间段、同一机位拍下的照片。两张图的地形轮廓完全一致，但天空的颜色和整体亮度差异明显。",
          "客观地说，这类差异在风景摄影里并不罕见。逆光拍摄、天气变化、手机与相机的宽容度差异，都会造成肉眼观感的不同。真正引发不满的，是成片里没有留下任何关于天气和时段的提示。",
        ],
      },
      {
        heading: "当事人的回应",
        paragraphs: [
          "8 月 28 日晚，山河小鹿发布了一条三分多钟的对比视频，把原片、调色过程和最终成片按顺序放出来。他承认调色幅度较大，并表示理解游客「白跑一趟」的感受。",
          "这条回应获得了不少宽容的评价。很多人认为，比起解释「后期是创作的一部分」，主动亮出原片本身就是一种诚意。",
        ],
      },
      {
        heading: "后续的小改变",
        paragraphs: [
          "8 月 30 日，博主在账号简介与每条旅行视频的开头，新增了实拍时间、天气与「含后期调色」的说明。这个小改动被不少同行效仿。",
          "讨论也慢慢从「该不该修图」转向了更有意义的问题：旅行内容需不需要标注拍摄条件。目前行业还没有统一答案，但至少有人先做了。",
        ],
      },
    ],
    followUp: [
      "博主账号已新增拍摄条件说明模板。",
      "多位旅行创作者跟进标注实拍天气与时段。",
      "事件热度回落，指南类内容讨论量上升。",
    ],
  },
  {
    id: "a-1006",
    title: "情侣账号「糖豆与阿岩」分手事件时间线：从删视频到双双停更",
    subtitle: "七天里发生了什么",
    summary:
      "一对人气情侣账号在七天内从同框更新到双双停更，期间删除了大量合拍视频，引发大量猜测。我们按时间线整理了可查证的公开信息。",
    category: "jingtian",
    tags: ["情侣账号", "删视频", "停更"],
    author: "瓜田记录员",
    publishedAt: "2026-08-18",
    updatedAt: "2026-08-27",
    readMinutes: 9,
    heat: 128500,
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20two%20empty%20chairs%20side%20by%20side%20on%20a%20warm%20lit%20studio%20set%20with%20soft%20curtains%2C%20no%20people%2C%20melancholic%20editorial%20mood%2C%20muted%20crimson%20and%20cream%20tones%2C%20cinematic%20grain&width=1000&height=640&seq=cover-6&orientation=landscape",
    coverAlt: "空置双椅的影棚氛围配图",
    featured: false,
    originalPost: {
      platform: "短视频平台",
      handle: "@账号观察日报",
      time: "8 月 18 日 22:40",
      text: "发现糖豆与阿岩的合拍视频少了一大半，最新一条也是单人出镜。先记录一下，不站队，等后续。",
    },
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=abstract%20empty%20video%20thumbnail%20grid%20with%20gray%20placeholder%20tiles%20on%20a%20dark%20interface%2C%20no%20readable%20text%2C%20muted%20editorial%20style%20with%20faint%20crimson%20accents&width=800&height=600&seq=shot-10&orientation=landscape",
        alt: "账号作品列表留空的抽象示意",
        caption: "账号作品列表出现大量空缺的示意",
      },
    ],
    timeline: [
      { time: "8 月 18 日", title: "合拍减少", desc: "网友发现合拍视频数量明显下降，出现单人内容。" },
      { time: "8 月 20 日", title: "账号静默", desc: "双方均未更新，评论区猜测增多。" },
      { time: "8 月 22 日", title: "首次发声", desc: "糖豆发布单人视频，称「最近在调整状态」。" },
      { time: "8 月 24 日", title: "另一当事人回应", desc: "阿岩发文表示双方已和平沟通，请勿打扰家人。" },
      { time: "8 月 27 日", title: "双双停更", desc: "两账号同时公告暂停更新一段时间。" },
    ],
    sections: [
      {
        heading: "为什么这件小事会被放大",
        paragraphs: [
          "情侣账号的特殊之处在于，粉丝关注的不是某一个人的内容，而是两个人之间的关系。一旦这种关系出现变化，粉丝的感受会更接近「围观一段真实生活」，也更容易产生情绪投入。",
          "正因为如此，一条删掉的视频、一次缺席的合拍，都会被解读出额外含义。本次事件中，大量讨论其实建立在推测上，缺乏可核实的信息。",
        ],
      },
      {
        heading: "可查证的公开信息有哪些",
        paragraphs: [
          "从公开内容看，可以确认的是：合拍视频数量减少、双方有数日未更新、两人先后发布过单人内容或文字说明。至于具体原因，双方都没有详细说明，只说「和平沟通」「调整状态」。",
          "这一点很重要。把可确认的信息和网上流传的猜测分开看，能避免被情绪化的说法带走。",
        ],
      },
      {
        heading: "停更之后",
        paragraphs: [
          "8 月 27 日，两个账号同时发布停更公告，措辞都比较克制，强调需要时间处理个人事务，并请关注者不要打扰双方家人。",
          "公告发布后，评论区的主流声音转向了理解。有粉丝留言说，账号可以等，生活更重要。这也是这类事件里少见的、相对温和的结局。",
        ],
      },
    ],
    followUp: [
      "双方账号仍处于停更状态，暂无复更时间表。",
      "部分搬运账号因传播未经证实的内容被平台处理。",
    ],
  },
  {
    id: "a-1007",
    title: "健身博主「铁手阿豪」课程退费争议：评论区为什么会炸",
    subtitle: "一份课程说明，两种理解",
    summary:
      "围绕训练营课程的退费规则，健身博主「铁手阿豪」与部分学员产生了分歧。我们把课程说明、学员反馈与后续方案整理清楚。",
    category: "mengliao",
    tags: ["退费", "训练营", "规则争议"],
    author: "瓜田记录员",
    publishedAt: "2026-08-12",
    updatedAt: "2026-08-19",
    readMinutes: 7,
    heat: 47600,
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20minimal%20gym%20interior%20with%20dumbbells%20and%20a%20chalk%20dust%20cloud%20in%20warm%20backlight%2C%20no%20people%2C%20moody%20editorial%20fitness%20photography%2C%20crimson%20and%20charcoal%20tones&width=1000&height=640&seq=cover-7&orientation=landscape",
    coverAlt: "健身房器械与暖光氛围配图",
    featured: false,
    originalPost: {
      platform: "图文社区",
      handle: "@练不动的小张",
      time: "8 月 12 日 21:15",
      text: "买课的时候只看到「不满意可协商」，报名后才知道有细则。不是说不能有规则，但规则能不能在付钱之前说清楚？",
    },
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=abstract%20printed%20terms%20document%20with%20blurred%20clauses%20and%20a%20pen%20resting%20on%20it%2C%20no%20readable%20text%2C%20warm%20desk%20lamp%20light%2C%20editorial%20contract%20photography&width=800&height=600&seq=shot-11&orientation=landscape",
        alt: "课程条款文件的抽象示意",
        caption: "学员提供的课程说明截图，细则部分此前未单独提示",
      },
    ],
    timeline: [
      { time: "8 月 12 日 21:15", title: "反馈帖出现", desc: "学员指出退费细则未在报名页明确提示。" },
      { time: "8 月 13 日", title: "集中讨论", desc: "多位学员在评论区补充各自情况，话题升温。" },
      { time: "8 月 15 日", title: "博主回应", desc: "铁手阿豪发布说明，承诺为有困难的学员单独协商。" },
      { time: "8 月 17 日", title: "方案公布", desc: "推出未开课全额退、已开课按课时比例退的方案。" },
      { time: "8 月 19 日", title: "报名页更新", desc: "课程页新增退费规则弹窗提示。" },
    ],
    sections: [
      {
        heading: "分歧点在哪里",
        paragraphs: [
          "争议的核心不是「能不能退」，而是「什么时候知道规则」。报名页的文案相对概括，细则藏在下单后的确认页里，导致部分学员是在付款之后才发现条款的。",
          "站在学员角度，这是信息不对称；站在博主角度，细则一直存在，只是展示位置不够显眼。两种说法都能成立，问题出在呈现方式上。",
        ],
      },
      {
        heading: "回应与处理方案",
        paragraphs: [
          "8 月 15 日，铁手阿豪发布了一份较长的说明，先承认展示位置确实不够醒目，然后给出了具体分类：尚未开课的可以全额退，已经开始的按已完成课时比例退，特殊情况单独协商。",
          "8 月 17 日，这份方案正式落地，并附带一个不算复杂但很实用的改动：报名页新增了退费规则弹窗，必须勾选确认才能进入支付环节。",
        ],
      },
      {
        heading: "这件事给内容付费的启示",
        paragraphs: [
          "知识付费和训练营类产品，最容易在「规则说明」上翻车。原因很简单：用户在付费那一刻的注意力，几乎全部在「能获得什么」，而不是「什么情况不能退」。",
          "把关键限制条件前置展示，短期看可能降低一点转化，长期看却能省掉大量售后纠纷。这次事件的处理，算是把这一点补上了。",
        ],
      },
    ],
    followUp: [
      "课程报名页已上线退费规则确认弹窗。",
      "博主公布了首批处理完成的退费名单（匿名）。",
    ],
  },
  {
    id: "a-1008",
    title: "穿搭博主「梨梨酱」设计稿对比事件：一场关于原创的拉锯",
    subtitle: "四张手稿，三个相似点",
    summary:
      "有设计师指出，穿搭博主「梨梨酱」联名系列中的几款图案与自己的早期手稿高度相似。双方就原创性问题来回沟通了数日。",
    category: "chaoji",
    tags: ["设计稿", "联名系列", "原创争议"],
    author: "瓜田记录员",
    publishedAt: "2026-08-05",
    updatedAt: "2026-08-16",
    readMinutes: 8,
    heat: 91300,
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20sketchbook%20open%20on%20a%20cream%20desk%20with%20fabric%20swatches%20and%20colored%20pencils%20scattered%20around%2C%20no%20people%2C%20warm%20editorial%20design%20studio%20photography%2C%20soft%20directional%20light&width=1000&height=640&seq=cover-8&orientation=landscape",
    coverAlt: "设计手稿与面料小样配图",
    featured: false,
    originalPost: {
      platform: "设计社区",
      handle: "@线稿收藏家",
      time: "8 月 5 日 10:30",
      text: "翻自己的旧作品集，发现有几个图案结构和某联名系列几乎一样。我保留了完整的创作过程文件，先把时间线放出来，等对方说明。",
    },
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=abstract%20four%20pattern%20sketches%20arranged%20in%20a%20grid%20with%20geometric%20motifs%2C%20no%20readable%20text%2C%20crimson%20pen%20annotations%20and%20arrows%2C%20editorial%20design%20comparison%20board&width=800&height=600&seq=shot-12&orientation=landscape",
        alt: "四张设计稿对比的抽象示意",
        caption: "设计师公布的早期手稿与联名图案对比",
      },
      {
        url: "https://readdy.ai/api/search-image?query=abstract%20layered%20design%20software%20layers%20panel%20and%20vector%20paths%20glowing%20on%20a%20dark%20monitor%2C%20no%20readable%20text%2C%20amber%20accents%2C%20editorial%20creative%20process%20photography&width=800&height=600&seq=shot-13&orientation=landscape",
        alt: "设计过程文件与图层的抽象示意",
        caption: "设计师提供的创作过程文件时间记录",
      },
    ],
    timeline: [
      { time: "8 月 5 日 10:30", title: "对比帖发布", desc: "设计师公布手稿与联名图案对比，要求说明来源。" },
      { time: "8 月 6 日", title: "品牌方回应", desc: "联名合作方表示图案由外部设计团队提供。" },
      { time: "8 月 8 日", title: "第三方介入", desc: "设计团队发布声明，称灵感来自公开素材库。" },
      { time: "8 月 11 日", title: "素材库比对", desc: "网友翻查素材库，未找到与手稿完全一致的版本。" },
      { time: "8 月 16 日", title: "协商收尾", desc: "双方宣布已就署名与后续合作达成一致。" },
    ],
    sections: [
      {
        heading: "相似点具体在哪里",
        paragraphs: [
          "对比帖列出三个相似点：主图形的分割比例、辅助纹样的排列间隔、以及一个不太常见的角部处理方式。前两点在图案设计里属于常见处理，第三点才是争议关键。",
          "设计师表示，这个角部处理是自己反复调整两周才确定的方案，它不算「独创技法」，但组合方式有个人偏好特征。这类判断很难有绝对答案，所以才需要过程文件。",
        ],
      },
      {
        heading: "三方说法为什么对不上",
        paragraphs: [
          "事件涉及设计师、博主、联名合作方和外部设计团队四方，信息链条一长，就容易出现责任模糊。品牌方说自己只是委托方，设计团队说灵感来自素材库，博主说自己只负责产品呈现。",
          "8 月 11 日，有网友翻查了被提到的素材库，没有找到与手稿完全一致的版本。这让「灵感来自公开素材」的说法变得站不住脚，讨论进一步升温。",
        ],
      },
      {
        heading: "最后的处理方式",
        paragraphs: [
          "8 月 16 日，设计师发布简短说明，称双方已就署名方式与后续合作达成一致，相关图案会在新一批产品的吊牌上标注原始设计来源。具体补偿细节没有公开。",
          "这场拉锯没有戏剧性的收尾，但过程本身说明了一件事：在设计类争议里，最能说话的不是情绪，而是带时间戳的过程文件。",
        ],
      },
    ],
    followUp: [
      "新一批产品吊牌将标注原始设计来源。",
      "合作方表示将完善外部设计稿的来源核查流程。",
    ],
  },
  {
    id: "a-1009",
    title: "多个账号同时停更：一次被过度解读的集体静默",
    subtitle: "同一周，六位创作者先后熄火",
    summary:
      "同一周内，六位分属不同领域的创作者先后宣布停更，网络上出现了多种猜测。我们核对了各账号的公开公告，发现原因并不相同。",
    category: "chaoji",
    tags: ["集体停更", "公告核对", "理性吃瓜"],
    author: "瓜田编辑部",
    publishedAt: "2026-07-28",
    updatedAt: "2026-08-09",
    readMinutes: 7,
    heat: 76500,
    cover:
      "https://readdy.ai/api/search-image?query=abstract%20row%20of%20dark%20monitors%20switched%20off%20in%20a%20quiet%20studio%2C%20faint%20amber%20standby%20lights%2C%20no%20people%2C%20moody%20editorial%20photography%2C%20cinematic%20shadows%20and%20dust%20motes&width=1000&height=640&seq=cover-9&orientation=landscape",
    coverAlt: "关闭的显示器排列配图",
    featured: false,
    originalPost: {
      platform: "站内编辑部",
      handle: "@瓜田编辑部",
      time: "7 月 28 日 15:00",
      text: "一周内六位创作者停更，各种说法都出来了。我们把每一条公告原文核了一遍，结论可能比传言无聊得多。",
    },
    screenshots: [
      {
        url: "https://readdy.ai/api/search-image?query=abstract%20six%20blurred%20announcement%20cards%20lined%20up%20in%20two%20columns%20on%20a%20dark%20background%2C%20no%20readable%20text%2C%20muted%20crimson%20and%20cream%20accents%2C%20editorial%20layout%20mock&width=800&height=600&seq=shot-14&orientation=landscape",
        alt: "六份公告并列的抽象示意",
        caption: "六位创作者的停更公告（内容已做模糊处理）",
      },
    ],
    timeline: [
      { time: "7 月 22 日", title: "第一位停更", desc: "美食创作者公告因身体原因暂停更新两周。" },
      { time: "7 月 24 日", title: "第二、第三位", desc: "旅行与穿搭创作者先后公告出国或换城市。" },
      { time: "7 月 26 日", title: "传言出现", desc: "网络上开始出现「集体封号」「联合抵制」等说法。" },
      { time: "7 月 28 日", title: "公告核对", desc: "编辑部逐条核对六份公告，原因各不相同。" },
      { time: "8 月 9 日", title: "陆续复更", desc: "已有四位创作者恢复更新，内容正常。" },
    ],
    sections: [
      {
        heading: "传言是怎么长出来的",
        paragraphs: [
          "当一个圈子里短时间内出现多个相似动作时，人本能地会寻找一个共同原因。六位创作者在同一周停更，客观上确实容易让人联想到「背后有事」。",
          "但与此同时，平台内容的高强度更新节奏，本来就意味着创作者需要按周期休息、调整、外出取材。这类停更每年都会发生，只是这次时间撞到了一起。",
        ],
      },
      {
        heading: "逐条核对后的结果",
        paragraphs: [
          "把六份公告放在一起看，原因分布相当分散：两位是健康与休息，两位是行程安排，一位是家庭事务，一位是内容改版。没有任何一条提到平台处罚或行业联合行动。",
          "更直接的证据是复更情况。到 8 月 9 日，已有四位创作者恢复正常更新，内容质量和频率都没有异常。如果真如传言所说，这种情况不会出现。",
        ],
      },
      {
        heading: "这类传言为什么总能传播",
        paragraphs: [
          "因为「巧合」听起来太普通，而「阴谋」听起来更有故事性。在缺少权威信息的情况下，人们更愿意相信那个解释力更强的版本。",
          "对读者来说，一个简单的判断方法是：先看有没有可核实的原始材料，再看有没有相反的证据。如果两条都缺失，那多半还不到下结论的时候。",
        ],
      },
    ],
    followUp: [
      "六位创作者中已有四位恢复更新。",
      "编辑部将「传言核查」整理为固定栏目，随热点更新。",
    ],
  },
];

export const getArticleById = (id: string): Article | undefined =>
  articles.find((item) => item.id === id);

export const getArticlesByCategory = (slug: string): Article[] =>
  articles.filter((item) => item.category === slug);

export const getFeaturedArticles = (): Article[] =>
  articles.filter((item) => item.featured);

export const getHotArticles = (limit = 5): Article[] =>
  [...articles].sort((a, b) => b.heat - a.heat).slice(0, limit);

export const getLatestArticles = (limit = 6): Article[] =>
  [...articles]
    .sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1))
    .slice(0, limit);

export const getRelatedArticles = (article: Article, limit = 3): Article[] =>
  articles
    .filter((item) => item.id !== article.id)
    .sort((a, b) => {
      const aScore = a.category === article.category ? 1 : 0;
      const bScore = b.category === article.category ? 1 : 0;
      return bScore - aScore;
    })
    .slice(0, limit);